import { useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { X, Mail, Instagram, Linkedin } from "lucide-react";
import { PageHero, Reveal, SectionHead, PhotoFrame, EmptyState, EASE } from "@/components/Shared";
import { useCollection, IMG } from "@/lib/api";

function TiltCard({ member, onClick, index }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 180, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 180, damping: 18 });

  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <motion.button
        ref={ref}
        onMouseMove={(e) => {
          const r = ref.current.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width);
          my.set((e.clientY - r.top) / r.height);
        }}
        onMouseLeave={() => {
          mx.set(0.5);
          my.set(0.5);
        }}
        style={{ rotateX: rx, rotateY: ry, transformPerspective: 800 }}
        onClick={onClick}
        className="card-line group w-full text-left"
        data-testid={`team-card-${member.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${index}`}
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <PhotoFrame
            src={member.photo}
            alt={member.name}
            label={`${member.name}'s photograph`}
            className="h-full w-full"
            imgClassName="transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="p-5">
          <h3 className="font-display text-xl font-semibold leading-tight text-silver">{member.name}</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ember">{member.position}</p>
          {member.intro && <p className="mt-3 text-xs leading-relaxed text-silver-muted">{member.intro}</p>}
        </div>
      </motion.button>
    </Reveal>
  );
}

function MemberModal({ member, onClose }) {
  return (
    <motion.div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="team-member-modal"
    >
      <motion.div
        className="card-line relative grid max-h-[90vh] w-full max-w-3xl gap-0 overflow-y-auto md:grid-cols-[0.8fr_1fr]"
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        transition={{ duration: 0.5, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
      >
        <PhotoFrame
          src={member.photo}
          alt={member.name}
          label={`${member.name}'s photograph`}
          className="aspect-[3/4] w-full bg-navy md:aspect-auto md:h-full"
        />
        <div className="p-8 sm:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{member.position}</p>
          <h3 className="mt-3 font-display text-3xl font-semibold metallic">{member.name}</h3>
          {member.department && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-silver-muted">{member.department}</p>}
          {member.intro && <p className="mt-5 text-sm leading-relaxed text-silver-dim">{member.intro}</p>}
          <p className="mt-5 text-xs italic text-silver-muted">
            [Bio, photograph and social links can be updated via the Admin panel.]
          </p>
          <div className="mt-8 flex gap-3">
            {[Mail, Instagram, Linkedin].map((Icon, i) => (
              <span key={i} className="flex h-10 w-10 items-center justify-center border border-silver/20 text-silver-muted" data-testid={`member-social-${i}`}>
                <Icon size={15} />
              </span>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="absolute right-4 top-4 text-silver-muted hover:text-white" aria-label="Close profile" data-testid="team-modal-close-btn">
          <X size={22} />
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function Team() {
  const { items, loading } = useCollection("team");
  const [selected, setSelected] = useState(null);

  const board = (items || []).filter((m) => m.group === "board");
  const chairs = (items || []).filter((m) => m.group === "chair");

  return (
    <main>
      <PageHero eyebrow="Our Team · 2026–27" title="The people behind" italic="the fire" image={IMG.forestMist}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Meet the Board of Directors and the department chairs leading RCSCW through the Forged year. Click any
          member to open their profile.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-24" data-testid="team-board">
        <SectionHead eyebrow="Core Team" />
        {loading && <p className="mt-10 text-sm text-silver-muted">Loading team…</p>}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {board.map((m, i) => (
            <TiltCard key={m.id} member={m} index={i} onClick={() => setSelected(m)} />
          ))}
        </div>
        {!loading && board.length === 0 && (
          <div className="mt-10">
            <EmptyState title="Board lineup coming soon" note="The 2026–27 board will be announced shortly." />
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28" data-testid="team-chairs">
        <SectionHead eyebrow="Board of Directors" title="The engine" italic="rooms" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {chairs.map((m, i) => (
            <TiltCard key={m.id} member={m} index={i} onClick={() => setSelected(m)} />
          ))}
        </div>
        {!loading && chairs.length === 0 && (
          <div className="mt-10">
            <EmptyState title="Departments forming" note="Chair profiles will be added as departments are finalised." />
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && <MemberModal member={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </main>
  );
}
